import { ICreateSendContentsState } from '../../types/createSendContents';
import { IGiftBudget } from '../../types/marketplace';

const isPersistableGiftBudget = (value: unknown): value is IGiftBudget => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const { value: amount } = value as Partial<IGiftBudget>;

  return typeof amount === 'number' && Number.isFinite(amount) && amount > 0;
};

/**
 * Repairs a rehydrated `contents` slice.
 *
 * `autoMergeLevel2` restores whatever is in storage verbatim, so `items` and `pygOptionOrder`
 * have to be proven arrays here: a corrupt `persist:createSend` would otherwise reach
 * `orderPygOptions` during render and white-screen the Contents step. A non-array `kits` is reset
 * to null (it would throw in `selectCsKitIds`), and a gift budget without a finite positive
 * amount is dropped so it no longer counts as a budget.
 *
 * A transform only ever sees its own slice, so it cannot read `method`. Dropping marketplace
 * picks and the budget for a Location draft is the reducer's job instead — see the
 * `SET_CS_METHOD` case in `reducers/contents.ts`, which also covers an in-session switch.
 */
export const normalizePersistedContents = (
  outbound: Partial<ICreateSendContentsState> | undefined,
): ICreateSendContentsState =>
  ({
    ...outbound,
    items: Array.isArray(outbound?.items) ? outbound?.items : [],
    pygOptionOrder: Array.isArray(outbound?.pygOptionOrder) ? outbound?.pygOptionOrder : [],
    kits: Array.isArray(outbound?.kits) ? outbound?.kits : null,
    giftBudget: isPersistableGiftBudget(outbound?.giftBudget) ? outbound?.giftBudget : null,
  } as ICreateSendContentsState);
